import React from 'react';
import { seasonalAdventures } from '../mock';
import ExperienceCard from '../components/ExperienceCard';
import { Sunrise, Mountain, Camera } from 'lucide-react';

const SeasonalAdventures = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1741642880695-a8ff2bef5f5a"
          alt="Summer landscape"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-6">
            Seasonal Light Adventures
          </h1>
          <p className="text-xl text-white/90 leading-relaxed">
            Following the light as seasons change
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            The Northern Lights are our winter compass. When the nights grow lighter, we shift with the seasons — into landscapes shaped by weather, atmosphere, and changing light.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            These Seasonal Light Adventures are about slowing down, noticing more, and learning photography through shared experience in beautiful places, without the intimidation or price tag of traditional photography tours.
          </p>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-brand-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-mint/30 rounded-full mb-4">
                <Sunrise className="text-brand-teal" size={28} />
              </div>
              <h3 className="text-xl font-normal text-gray-900 mb-3">
                Transitional Light
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Explore how light changes with the seasons, from long summer evenings to dramatic spring conditions.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-mint/30 rounded-full mb-4">
                <Mountain className="text-brand-teal" size={28} />
              </div>
              <h3 className="text-xl font-normal text-gray-900 mb-3">
                Remarkable Places
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Scottish Highlands, Hebridean Islands, Iceland, Norway, Patagonia — landscapes that invite exploration.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-mint/30 rounded-full mb-4">
                <Camera className="text-brand-teal" size={28} />
              </div>
              <h3 className="text-xl font-normal text-gray-900 mb-3">
                Learning by Doing
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Photography instruction that focuses on seeing and creating, not technical complexity or expensive gear.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Adventures Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 mb-12 text-center">
            Where Light Takes Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {seasonalAdventures.map((adventure) => (
              <ExperienceCard key={adventure.id} experience={adventure} type="seasonal" />
            ))}
          </div>
        </div>
      </section>

      {/* Seasonal Rhythm Section */}
      <section className="py-16 bg-brand-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 mb-12 text-center">
            Moving with the Seasons
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <img
                src="https://images.unsplash.com/photo-1628946329352-4b4e9fcbfa6a"
                alt="Scottish Highlands"
                className="rounded-lg shadow-lg w-full h-80 object-cover mb-6"
              />
              <h3 className="text-2xl font-light text-gray-900 mb-3">Spring & Early Summer</h3>
              <p className="text-gray-600 leading-relaxed">
                Scottish Highlands and Hebridean Islands come alive with dramatic weather, soft light, and landscapes that shift between moods. Perfect for learning to work with changing conditions.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1562529074-e3ec282e9b60"
                alt="Iceland summer"
                className="rounded-lg shadow-lg w-full h-80 object-cover mb-6"
              />
              <h3 className="text-2xl font-light text-gray-900 mb-3">Summer</h3>
              <p className="text-gray-600 leading-relaxed">
                Iceland, Norway, and Alaska under the midnight sun. Long days of golden light, dramatic waterfalls, and landscapes that never sleep. Time to explore without the rush.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who These Are For */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            For Curious Explorers
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            These adventures are for people who want to slow down and notice more. Who are curious about light, landscape, and creativity. Who want to learn photography without feeling like they need £5,000 worth of gear or a degree in technical settings.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            Small groups (4-6 people), accessible pricing (£895-£2,850), and a focus on experience over expertise. You don't need to be an expert — just willing to explore.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SeasonalAdventures;